import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { Amplify } from "aws-amplify";

import awsConfig from "./aws-exports";
import store from "./redux/store";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import reportWebVitals from "./reportWebVitals";
import { hasAnalyticsConsent } from "./services/consentService";

import "./styles/reset.css";
import "./styles/tokens.css";
import "./styles/global.css";
import "./styles/global-layout.css";
import "./styles/buttons.css";
import "./styles/cards.css";
import "./styles/forms.css";
import "./styles/toasts.css";
import "./styles/a11y.css";
import "./styles/page-hero.css";
import "./styles/boot-loading.css";
import "./App.css";
import "./pages/Shared/marketing-theme.css";

/* ------------------------------------------------------------
   1) Amplify config
------------------------------------------------------------- */

Amplify.configure({
  ...awsConfig,
  Auth: {
    ...(awsConfig?.Auth || {}),
    mandatorySignIn: true,
    authenticationFlowType: "USER_SRP_AUTH",
  },
});

/* ------------------------------------------------------------
   2) Crash-proofing: chunk recovery + global guards
------------------------------------------------------------- */

const CHUNK_RECOVERY_INSTALLED_FLAG = "__IF_CHUNK_RECOVERY_INSTALLED__";
const GLOBAL_GUARDS_INSTALLED_FLAG = "__IF_GLOBAL_GUARDS_INSTALLED__";
const RELOAD_ONCE_KEY = "if_chunk_reload_once";
const RELOAD_COOLDOWN_KEY = "if_chunk_reload_cooldown_until";
const RELOAD_COOLDOWN_MS = 10_000;
const ERROR_LOG_DEDUPE_MS = 1_500;

function safeErrorMessage(value) {
  const raw = String(value?.message || value || "");

  return raw
    .replace(/Bearer\s+[A-Za-z0-9\-_\.]+/gi, "Bearer [REDACTED]")
    .replace(/access[_-]?token=([^&\s]+)/gi, "access_token=[REDACTED]")
    .replace(/id[_-]?token=([^&\s]+)/gi, "id_token=[REDACTED]")
    .replace(/refresh[_-]?token=([^&\s]+)/gi, "refresh_token=[REDACTED]")
    .slice(0, 600);
}

function isChunkLoadErrorMessage(message) {
  const m = String(message || "");
  return (
    /ChunkLoadError/i.test(m) ||
    /Loading chunk \d+ failed/i.test(m) ||
    /Failed to fetch dynamically imported module/i.test(m) ||
    /Importing a module script failed/i.test(m)
  );
}

function canUseSessionStorage() {
  try {
    return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
  } catch {
    return false;
  }
}

function canReloadForChunkFailure() {
  if (!canUseSessionStorage()) return true;

  try {
    const alreadyReloaded = sessionStorage.getItem(RELOAD_ONCE_KEY) === "1";
    if (alreadyReloaded) return false;

    const cooldownUntil = Number(sessionStorage.getItem(RELOAD_COOLDOWN_KEY) || "0");
    if (cooldownUntil && Date.now() < cooldownUntil) return false;

    return true;
  } catch {
    return true;
  }
}

function markChunkReloadAttempt() {
  if (!canUseSessionStorage()) return;

  try {
    sessionStorage.setItem(RELOAD_ONCE_KEY, "1");
    sessionStorage.setItem(
      RELOAD_COOLDOWN_KEY,
      String(Date.now() + RELOAD_COOLDOWN_MS)
    );
  } catch {
    // ignore storage failures
  }
}

function reloadOnceForChunkFailure() {
  if (typeof window === "undefined") return;
  if (!canReloadForChunkFailure()) return;

  markChunkReloadAttempt();
  window.location.reload();
}

function installChunkLoadRecovery() {
  if (typeof window === "undefined") return;
  if (window[CHUNK_RECOVERY_INSTALLED_FLAG]) return;

  window[CHUNK_RECOVERY_INSTALLED_FLAG] = true;

  window.addEventListener("error", (event) => {
    const msg = safeErrorMessage(event?.message || event?.error?.message);
    if (isChunkLoadErrorMessage(msg)) {
      reloadOnceForChunkFailure();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const msg = safeErrorMessage(event?.reason);
    if (isChunkLoadErrorMessage(msg)) {
      reloadOnceForChunkFailure();
    }
  });
}

function installGlobalGuards() {
  if (typeof window === "undefined") return;
  if (window[GLOBAL_GUARDS_INSTALLED_FLAG]) return;

  window[GLOBAL_GUARDS_INSTALLED_FLAG] = true;

  const seen = new Map();

  const shouldLog = (msg) => {
    const now = Date.now();
    const last = seen.get(msg) || 0;

    if (now - last < ERROR_LOG_DEDUPE_MS) return false;

    seen.set(msg, now);
    return true;
  };

  window.addEventListener("unhandledrejection", (event) => {
    const msg = safeErrorMessage(event?.reason);
    if (!msg) return;

    if (shouldLog(msg)) {
      // eslint-disable-next-line no-console
      console.error("[unhandledrejection]", msg);
    }
  });

  window.addEventListener("error", (event) => {
    const msg = safeErrorMessage(event?.error || event?.message);
    if (!msg) return;

    if (shouldLog(msg)) {
      // eslint-disable-next-line no-console
      console.error("[window.error]", msg);
    }
  });
}

installChunkLoadRecovery();
installGlobalGuards();

/* ------------------------------------------------------------
   3) App lazy load + boot fallback
------------------------------------------------------------- */

const App = React.lazy(() => import("./App"));

function BootLoadingScreen() {
  return (
    <div
      className="loading if-boot"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="if-boot__card">
        <div className="if-boot__top">
          <div className="if-boot__brand">LocReach</div>
          <div className="if-boot__pill">UK Hyper-Local AI</div>
        </div>

        <div className="if-boot__msg">Loading your influencer workspace…</div>
        <div className="if-boot__sub">Restoring saved searches and filters.</div>

        <div className="if-boot__bars" aria-hidden="true">
          <div className="if-boot__bar skel" />
          <div className="if-boot__bar if-boot__bar--sm skel" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------
   4) Root render
------------------------------------------------------------- */

function normalizeBasename(value) {
  const raw = String(value || "/").trim();

  if (!raw) return "/";
  if (/^https?:\/\//i.test(raw)) return "/";
  if (raw.startsWith("//")) return "/";

  const withLeadingSlash = raw.startsWith("/") ? raw : `/${raw}`;

  return withLeadingSlash.length > 1
    ? withLeadingSlash.replace(/\/+$/, "")
    : "/";
}

const basename = normalizeBasename(process.env.REACT_APP_BASENAME);

function Providers({ children }) {
  return (
    <Provider store={store}>
      <AuthProvider>{children}</AuthProvider>
    </Provider>
  );
}

const rootEl = document.getElementById("root");

if (!rootEl) {
  throw new Error("Root element #root not found");
}

const root = ReactDOM.createRoot(rootEl);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter basename={basename}>
          <Providers>
            <Suspense fallback={<BootLoadingScreen />}>
              <App />
            </Suspense>
          </Providers>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

/* ------------------------------------------------------------
   5) Web vitals
------------------------------------------------------------- */

reportWebVitals((metric) => {
  const isProd = process.env.NODE_ENV === "production";

  if (!isProd) {
    // eslint-disable-next-line no-console
    console.log("[vitals]", metric);
    return;
  }

  if (!hasAnalyticsConsent()) return;
  if (Math.random() > 0.1) return;

  try {
    const payload = JSON.stringify({
      name: metric.name,
      value: metric.value,
      id: metric.id,
      rating: metric.rating,
      navType: metric.navigationType,
    });

    const blob = new Blob([payload], { type: "application/json" });

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/rum/vitals", blob);
    }
  } catch {
    // ignore
  }
});